import { z } from "zod";
import { PropertyUI } from "./lib/meta-types";

const eventsParamDescription = `
List of events to send, delimited by comma. Following <code>page</code>, <code>screen</code>, or any arbitrary event (name of <code>track</code> event).
Special values: <b>empty string</b> - send only <code>track</code> events, <b>*</b> - send all events - this is useful if you want to filter events with Functions.
`;
export const FacebookConversionApiCredentials = z.object({
  pixelId: z.string().describe("Facebook Pixel ID"),
  accessToken: z.string().describe("Facebook Access Token"),
  actionSource: z
    .enum(["email", "website", "app", "phone_call", "chat", "physical_store", "system_generated", "other"])
    .default("website")
    .describe("Action Source"),
  events: z.string().optional().default("").describe(eventsParamDescription),
  productContentIdField: z
    .enum(["sku", "product_id", "variant_id"])
    .default("sku")
    .describe("Product Content ID Field"),
  phoneFieldName: z
    .string()
    .optional()
    .default("")
    .describe(
      "Name of the field in the event user traits that contains the phone number. Expected format could be <a href='https://en.wikipedia.org/wiki/E.164' target='_blank' rel='noreferrer noopener'>E.164</a> or international format. If empty, phone number hash will not be sent."
    ),
});

export const FacebookConversionApiCredentialsUi: Partial<Record<keyof FacebookConversionApiCredentials, PropertyUI>> = {
  accessToken: {
    password: true,
  },
};

export type FacebookConversionApiCredentials = z.infer<typeof FacebookConversionApiCredentials>;

export const WebhookDestinationConfig = z.object({
  url: z.string().url().describe("Webhook URL"),
  method: z
    .enum(["GET", "POST", "PUT", "DELETE"])
    .default("POST")
    .describe("HTTP method. Can be <code>GET</code>, <code>POST</code>, <code>PUT</code>, <code>DELETE</code>"),
  headers: z.array(z.string()).optional().describe("List of headers in format <code>key: value</code>"),
  customPayload: z
    .boolean()
    .optional()
    .default(false)
    .describe("Enable custom payload. If disabled, the event payload will be sent as is."),
  payload: z
    .string()
    .optional()
    .describe(
      "Payload Template::Template for the webhook payload. The following macros are supported:<ul><li><code>{{ EVENT }}</code> - event json object for stream mode or batches with size=1</li><li><code>{{ EVENTS }}</code> - for batch mode - json array of events</li><li><code>{{ EVENTS_COUNT }}</code> - count of events in batch</li><li><code>{{ NAME }}</code> - event name</li><li><code>{{ env.VAR_NAME }}</code> - value of VAR_NAME environment variable</li></ul>"
    ),
  sendTrack: z.boolean().optional().default(true).describe("Send Track events to this destination"),
  sendIdentify: z.boolean().optional().default(true).describe("Send Identify events to this destination"),
  sendPage: z.boolean().optional().default(true).describe("Send Page events to this destination"),
});

export type WebhookDestinationConfig = z.infer<typeof WebhookDestinationConfig>;

const MixpanelServiceAccountDocumentation =
  'See <a href="https://developer.mixpanel.com/reference/service-accounts">how to create service account</a>';

export const IntercomDestinationCredentials = z.object({
  accessToken: z
    .string()
    .describe(
      "Intercom Access Token. You should first create an app in Intercom Developer Hub, and then generate an access token in the app settings. See <a href='https://developers.intercom.com/docs/build-an-integration/getting-started/' target='_blank' rel='noreferrer noopener'>a detailed guide</a>"
    ),
  updateLastSeenOnEveryEvent: z
    .boolean()
    .optional()
    .describe(
      "By default, the last seen property will be updated only on .identify() calls. If enabled, the property will be updated on every event. However, enabling this option may lead to higher API usage."
    ),
});

export const IntercomDestinationCredentialsUi: Partial<
  Record<keyof z.infer<typeof IntercomDestinationCredentials>, PropertyUI>
> = {
  accessToken: {
    password: true,
  },
};

export type IntercomDestinationCredentials = z.infer<typeof IntercomDestinationCredentials>;

export const MixpanelCredentials = z.object({
  simplifiedIdMerge: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      `Simplified Identity Merge::Use Mixpanel <a href="https://docs.mixpanel.com/docs/tracking-methods/identifying-users">Simplified Identity Merge</a> feature.<br/>Enable this option if your Mixpanel project has the corresponding <a href="https://docs.mixpanel.com/docs/tracking-methods/identifying-users#how-do-i-switch-between-the-simplified-and-original-api">feature enabled</a>.<br/><b>Using this feature is highly recommended to achieve better quality Identity Merge</b>`
    ),
  projectId: z
    .string()
    .describe(
      'Project id can be found in the <a href="https://help.mixpanel.com/hc/en-us/articles/115004490503-Project-Settings">project settings</a>'
    ),
  projectToken: z
    .string()
    .describe('See <a href="https://developer.mixpanel.com/reference/project-token">how to obtain project secret</a>'),
  //apiSecret: z.string(),
  serviceAccountUserName: z.string().describe(MixpanelServiceAccountDocumentation),
  serviceAccountPassword: z.string().describe(MixpanelServiceAccountDocumentation),
  sendPageEvents: z
    .boolean()
    .optional()
    .default(true)
    .describe("If enabled, all page view events will be sent to Mixpanel."),
  sendIdentifyEvents: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "If enabled, any identify() call will send an Identify event to Mixpanel in addition to the profile update"
    ),
  enableGroupAnalytics: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Mixpanel Group Analytics allows behavioral data analysis at a customized group level. Group Analytics is available as an add-on package to customers on <a href='https://mixpanel.com/pricing/' target='_blank' rel='noreferrer noopener'>Growth and Enterprise plans.</a>"
    ),
  filterBotTraffic: z.boolean().optional().default(true).describe("Don't send traffic from known bots to Mixpanel"),
  groupKey: z
    .string()
    .optional()
    .default("$group_id")
    .describe(
      "Group Key for Mixpanel Group Analytics. Make sure that Group Key in <a href='https://mixpanel.com/report' target='_blank' rel='noreferrer noopener'>Mixpanel project settings</a> matches the provided value."
    ),
  enableAnonymousUserProfiles: z
    .boolean()
    .optional()
    .default(false)
    .describe("If enabled, anonymous users will be tracked in Mixpanel"),
});
export type MixpanelCredentials = z.infer<typeof MixpanelCredentials>;

export const MixpanelCredentialsUi: Partial<Record<keyof MixpanelCredentials, PropertyUI>> = {
  serviceAccountPassword: {
    password: true,
  },
};

export const JuneCredentials = z.object({
  apiKey: z
    .string()
    .describe(
      `API Key::To get or create an API Key, go to workspace's "Settings & integrations" > Integrations > June SDK`
    ),
  enableAnonymousUserProfiles: z
    .boolean()
    .optional()
    .default(false)
    .describe("If enabled, anonymous users will be tracked in June"),
});

export const JuneCredentialsUi: Partial<Record<keyof JuneCredentials, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

export type JuneCredentials = z.infer<typeof JuneCredentials>;

export const SalesforceCredentials = z.object({
  authorized: z.boolean().optional().default(false),
  oauthIntegrationId: z.string().optional().default("jitsu-cloud-dst-salesforce"),
  oauthConnectionId: z.string().optional(),
  isSandbox: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Sandbox Instance::You can log in to a sandbox by switching the login form to use Custom Domain, and providing the login URL of your sandbox, e.g.: <b>MyDomainName--SandboxName.sandbox.my.salesforce.com</b><br/><br/>Alternatively, you can use the general login form by appending the sandbox name to your Salesforce username. For example, if the production username is <b>user@example.com</b> and the sandbox is named <b>sndbx</b>, the sandbox username would be: <b>user@example.com.sndbx</b><br/><br/>If you are already authenticated, please <b>Re-Sign In</b> with your sandbox username."
    ),
});
export type SalesforceCredentials = z.infer<typeof SalesforceCredentials>;

// Configuration and credentials for Braze _CLOUD-MODE_ destination.
export const BrazeCredentials = z.object({
  apiKey: z
    .string()
    .describe(
      "API Key::Created under Developer Console in the Braze Dashboard. Must have User Data and Events permissions."
    ),
  endpoint: z
    .enum([
      "US-01 : dashboard-01.braze.com",
      "US-02 : dashboard-02.braze.com",
      "US-03 : dashboard-03.braze.com",
      "US-04 : dashboard-04.braze.com",
      "US-05 : dashboard-05.braze.com",
      "US-06 : dashboard-06.braze.com",
      "US-07 : dashboard-07.braze.com",
      "US-08 : dashboard-08.braze.com",
      "US-09 : dashboard-09.braze.com",
      "EU-01 : dashboard-01.braze.eu",
      "EU-02 : dashboard-02.braze.eu",
    ])
    .optional()
    .default("US-01 : dashboard-01.braze.com")
    .describe(
      "Your Braze REST endpoint. <a target='_blank' rel='noopener noreferrer' href='https://www.braze.com/docs/api/basics/#endpoints'>See more details</a>"
    ),
  appId: z
    .string()
    .optional()
    .describe(
      "App ID::The app identifier used to reference specific Apps in requests made to the Braze API. Created under Developer Console in the Braze Dashboard."
    ),
  useJitsuAnonymousIdAlias: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Use Anonymous Id Alias::Use Jitsu <code>anonymousId</code> as an alias for identified and anonymous profiles. Enables support for anonymous (alias-only) profiles."
    ),
  sendPageEvents: z
    .boolean()
    .optional()
    .default(false)
    .describe("Send <code>page</code> and <code>screen</code> events as Braze Custom Events"),
  purchaseEventNames: z
    .array(z.string())
    .optional()
    .default(["Order Completed", "Order Cancelled", "Order Refunded"])
    .describe(
      "Purchase Event Names::List of event names that should trigger Braze purchase tracking. Default: Order Completed, Order Cancelled, Order Refunded"
    ),
  profileUpdateEventNames: z
    .array(z.string())
    .optional()
    .default(["Consent Preferences Updated"])
    .describe(
      "Profile Update Event Names::List of track event names that should also update user profiles (in addition to tracking the event). Example: Consent Preferences Updated"
    ),
  sendCustomAttributes: z.boolean().optional().default(false).describe("Send custom attributes to Braze"),
});

export const BrazeCredentialsUi: Partial<Record<keyof BrazeCredentials, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};
export type BrazeCredentials = z.infer<typeof BrazeCredentials>;

// Configuration and credentials for Braze _DEVICE-MODE_ destination.
export const BrazeDeviceCredentials = z.object({
  sdkVersion: z
    .enum([
      "4.1",
      "4.6",
      "4.8",
      "4.10",
      "5.4",
      "5.7",
      "5.8",
      "5.9",
      "6.0",
      "6.1",
      "6.2",
      "6.3",
      "6.4",
      "6.5",
      "6.6",
      "6.7",
      "6.8",
    ])
    .default("6.8")
    .describe("The version of the Braze SDK to use"),
  apiKey: z
    .string()
    .describe(
      "Note that this key is distinct from your Braze 'Classic' API key. Found in the Braze Dashboard under Manage Settings → Apps → Web. SDK API keys are not secret."
    ),
  endpoint: z
    .enum([
      "US-01 : dashboard-01.braze.com",
      "US-02 : dashboard-02.braze.com",
      "US-03 : dashboard-03.braze.com",
      "US-04 : dashboard-04.braze.com",
      "US-05 : dashboard-05.braze.com",
      "US-06 : dashboard-06.braze.com",
      "US-07 : dashboard-07.braze.com",
      "US-08 : dashboard-08.braze.com",
      "US-09 : dashboard-09.braze.com",
      "EU-01 : dashboard-01.braze.eu",
      "EU-02 : dashboard-02.braze.eu",
    ])
    .optional()
    .default("US-01 : dashboard-01.braze.com")
    .describe(
      "Your Braze SDK endpoint. <a target='_blank' rel='noopener noreferrer' href='https://www.braze.com/docs/user_guide/administrative/access_braze/sdk_endpoints'>See more details</a>"
    ),
  debug: z.boolean().default(false).describe("If enabled, the Braze SDK will log useful messages to the console."),
  enableOrderCompletedCalls: z
    .boolean()
    .default(true)
    .describe("Event type = 'track' and event = 'Order Completed'<br> Triggers Track Purchase"),
  enableTrackCalls: z
    .boolean()
    .default(true)
    .describe("Event type = 'track' and event != 'Order Completed' <br> Triggers Track Event"),
  enableIdentifyCalls: z
    .boolean()
    .default(true)
    .describe("Event type = 'identify'<br> Event type = 'group'<br> Triggers Update User Profile"),
  sendPageEvents: z
    .boolean()
    .default(false)
    .describe(
      "Event type = 'page' or 'screen'<br> Sends page/screen views as custom events. Note: Each page view consumes a Braze data point."
    ),
  pageEventName: z
    .string()
    .optional()
    .describe(
      "Page Event Name::Custom event name for page views. If not set, defaults to 'page' for page events and 'screen' for screen events. Example: 'Page Viewed'"
    ),
  allowUserSuppliedJavascript: z
    .boolean()
    .optional()
    .default(false)
    .describe("Trust the Braze dashboard users to write JavaScript code for click actions."),
  automaticallySendInAppMessages: z
    .boolean()
    .optional()
    .default(true)
    .describe("If enabled, in-app messages will be automatically displayed when received."),
  eagerInit: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Eager SDK Initialization::If enabled, the Braze SDK will be initialized immediately when the first analytics event is sent, before event processing. Enable this if you need direct access to <code>window.braze</code> for features like banners or in-app messages."
    ),
  enableSdkAuthentication: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "SDK Authentication::If enabled, the Braze SDK is initialized with SDK Authentication turned on and a server-signed JWT is passed to Braze. Your app must sign the JWT with your Braze private key and supply it on identify via <code>context.braze.sdkAuthSignature</code>. Optionally register <code>window.__chordBrazeTokenRefresh</code> to auto-refresh expired tokens."
    ),
});

export type BrazeDeviceCredentials = z.infer<typeof BrazeDeviceCredentials>;

export const SegmentCredentials = z.object({
  apiBase: z.string().default("https://api.segment.io/v1").describe("API Base::Segment API Base"),
  writeKey: z
    .string()
    .describe(
      `To get an API Key you need to add the HTTP API source to your Segment workspace. Write Key can be found on the HTTP API source Overview page.`
    ),
});

export const SegmentCredentialsUi: Partial<Record<keyof SegmentCredentials, PropertyUI>> = {
  writeKey: {
    password: true,
  },
};
export type SegmentCredentials = z.infer<typeof SegmentCredentials>;

export const POSTHOG_DEFAULT_HOST = "https://app.posthog.com";

export const PosthogDestinationConfig = z.object({
  key: z
    .string()
    .describe(
      "Project API Key::Posthog Project API Key. Can be found in <a target='_blank' rel='noopener noreferrer' href='https://app.posthog.com/project/settings'>Project Settings</a>"
    ),
  host: z.string().optional().default(POSTHOG_DEFAULT_HOST).describe("Posthog host"),
  enableGroupAnalytics: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Group analytics is a powerful feature in PostHog that allows you to perform analytics on entities other than single users. Group Analytics is not available on the open-source or free cloud plan. <a href='https://posthog.com/pricing' target='_blank' rel='noreferrer noopener'>Learn more.</a>"
    ),
  groupType: z
    .string()
    .optional()
    .default("chord_group")
    .describe(
      "Group type is the abstract type of whatever our group represents (e.g. company, team, chat, post, etc.). <a href='https://posthog.com/docs/getting-started/group-analytics#groups-vs-group-types' target='_blank' rel='noreferrer noopener'>Groups vs. group types.</a>"
    ),
  sendAnonymousEvents: z
    .boolean()
    .optional()
    .default(false)
    .describe("Send events from anonymous users. If disabled, only events from identified users will be tracked."),
  enableAnonymousUserProfiles: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Enable Anonymous Person Profiles::Create <a href='https://posthog.com/docs/getting-started/person-properties' target='_blank' rel='noreferrer noopener'>Person profiles</a> for anonymous users. If disabled, only identified users will have profiles."
    ),
});

export const PosthogDestinationConfigUi: Partial<Record<keyof PosthogDestinationConfig, PropertyUI>> = {
  sendAnonymousEvents: {
    // assumes value of this freshly added property from the value of the `enableAnonymousUserProfiles` property
    correction: obj =>
      typeof obj?.sendAnonymousEvents === "undefined" ? obj?.enableAnonymousUserProfiles : obj?.sendAnonymousEvents,
  },
  enableAnonymousUserProfiles: {
    hidden: obj =>
      typeof obj?.sendAnonymousEvents === "undefined" ? !obj.enableAnonymousUserProfiles : !obj.sendAnonymousEvents,
  },
  key: {
    password: true,
  },
};

export type PosthogDestinationConfig = z.infer<typeof PosthogDestinationConfig>;

export const AmplitudeDestinationConfig = z.object({
  key: z.string().describe("Project API Key::Amplitude Project API Key."),
  enableGroupAnalytics: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Build an analysis around aggregated units of measure like accounts, charts, or order IDs. Requires The Amplitude Accounts add-on. <a href='https://help.amplitude.com/hc/en-us/articles/115001765532-Account-level-reporting-in-Amplitude' target='_blank' rel='noreferrer noopener'>Learn more.</a>"
    ),
  groupType: z
    .string()
    .optional()
    .default("company")
    .describe(
      "Group type is the abstract type of whatever our group represents (e.g. accounts, charts, or order IDs)."
    ),
  enableAnonymousUserProfiles: z
    .boolean()
    .optional()
    .default(false)
    .describe("If enabled, anonymous users will be tracked in Amplitude"),
  dataResidency: z.enum(["US", "EU"]).optional().default("US"),
  sessionWindow: z.number().optional().default(30).describe("Session window in minutes"),
});
export const AmplitudeDestinationConfigUi: Partial<
  Record<keyof z.infer<typeof AmplitudeDestinationConfig>, PropertyUI>
> = {
  key: {
    password: true,
  },
};

export type AmplitudeDestinationConfig = z.infer<typeof AmplitudeDestinationConfig>;

export const AmplitudeActionsCredentials = z.object({
  apiKey: z.string().describe("Project API Key::Amplitude Project API Key. Found in Data > Sources & Destinations."),
  secretKey: z
    .string()
    .optional()
    .describe(
      "Project Secret Key::Required for group identify and user mapping. Found in Data > Sources & Destinations."
    ),
  endpoint: z
    .enum(["north_america", "europe"])
    .optional()
    .default("north_america")
    .describe("Region to send data. Select 'europe' if your Amplitude project is in the EU data center."),
  propertySplitting: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Property Splitting::When disabled (default), Order Completed events with a products array will generate individual Product Purchased events for each product. When enabled, only the Order Completed event is sent — enable this if property splitting is active in your Amplitude project, as Amplitude will handle splitting the products array automatically."
    ),
  actionMappings: z
    .array(
      z.object({
        sourceEvent: z
          .string()
          .describe(
            "Source Event(s)::Event(s) to match. Comma-separated for multiple (e.g., <code>Purchase,Subscription,Renewal</code>). Use event name for track events, type for others (identify, page), or <code>*</code> for all."
          ),
        action: z
          .enum(["logEventV2", "identifyUser", "logPurchase", "mapUser", "groupIdentifyUser", "logEvent"])
          .describe(
            "Amplitude Action::logEventV2 = Track event, identifyUser = Update user, logPurchase = Track revenue, mapUser = Merge identities, groupIdentifyUser = Update group"
          ),
        targetEvent: z
          .string()
          .optional()
          .describe(
            "Target Event Name::Override the event name sent to Amplitude. Leave empty to use the original name. Use <code>$drop</code> to filter/exclude the event."
          ),
        fields: z
          .array(
            z.object({
              sourceProperty: z
                .string()
                .describe(
                  "Source Property::Path in your event. Supports dot notation (properties.product_id) and array wildcards (properties.products[*].sku)."
                ),
              targetProperty: z
                .string()
                .describe(
                  "Target Property::Amplitude field name (e.g., event_properties.item_id, user_properties.$set.plan, revenue)."
                ),
            })
          )
          .optional()
          .describe("Field Mappings::Map properties from your events to Amplitude fields."),
      })
    )
    .optional()
    .describe(
      "Custom Action Mappings::Override default routing. Defaults: track → logEventV2, identify → identifyUser, page/screen → logEventV2, group → groupIdentifyUser, alias → mapUser, Order Completed → logPurchase"
    ),
});

export const AmplitudeActionsCredentialsUi: Partial<
  Record<keyof z.infer<typeof AmplitudeActionsCredentials>, PropertyUI>
> = {
  apiKey: {
    password: true,
  },
  secretKey: {
    password: true,
  },
};

export type AmplitudeActionsCredentials = z.infer<typeof AmplitudeActionsCredentials>;

export const MongodbDestinationConfig = z.object({
  url: z.string().optional(),
  protocol: z
    .enum(["mongodb", "mongodb+srv"])
    .default("mongodb")
    .describe(
      "MongoDB protocol. <code>mongodb</code> or <code>mongodb+srv</code>. For Atlas use <code>mongodb+srv</code>"
    ),
  hosts: z
    .array(z.string())
    .optional()
    .describe("MongoDB hosts with port (e.g. <code>localhost:27017</code>). One on each line"),
  username: z.string().describe("MongoDB username"),
  password: z.string().describe("MongoDB password"),
  database: z.string().describe("MongoDB database"),
  collection: z.string().describe("MongoDB collection"),

  options: z.object({}).catchall(z.string().default("")).optional().describe("Additional MongoDB connection options."),
});

export const MongodbDestinationConfigUi: Partial<Record<keyof MongodbDestinationConfig, PropertyUI>> = {
  hosts: {
    editor: "StringArrayEditor",
  },
  url: {
    hidden: true,
  },
  password: {
    password: true,
  },
};

export type MongodbDestinationConfig = z.infer<typeof MongodbDestinationConfig>;

export const Ga4Credentials = z.object({
  apiSecret: z
    .string()
    .describe(
      "An API SECRET generated in the Google Analytics UI, navigate to: Admin > Data Streams > choose your stream > Measurement Protocol > Create"
    ),
  measurementId: z
    .string()
    .describe(
      "The Measurement ID associated with a stream. Found in the Google Analytics UI under: Admin > Data Streams > choose your stream > Measurement ID. <b>Required for web streams.</b>"
    ),
  firebaseAppId: z
    .string()
    .optional()
    .describe(
      "The Firebase App ID associated with the Firebase app. Found in the Firebase console under: Project Settings > General > Your Apps > App ID. <b>Required for mobile app streams.</b>"
    ),
  url: z
    .string()
    .url()
    .describe(
      "Measurement Protocol URL.<br/>Default: <code>https://www.google-analytics.com/mp/collect</code><br/>Default debug url: <code>https://www.google-analytics.com/debug/mp/collect</code>"
    )
    .default("https://www.google-analytics.com/mp/collect"),
  events: z.string().optional().default("").describe(eventsParamDescription),
  forwardExtraProperties: z
    .boolean()
    .default(false)
    .describe(
      "Forward extra event properties::When enabled, event properties not covered by the built-in GA4 mapping or your configured custom mappings are forwarded to GA4 automatically. Mapped properties always take precedence."
    ),
  userIdClientIdFallback: z
    .boolean()
    .default(true)
    .describe(
      "Use User ID as client ID fallback::When an event has no GA4 client ID or anonymous ID, which is typical for server-side events, send its User ID as the client ID instead of skipping the event. GA4 will not attribute these events to the user's web session."
    ),
});

export const Ga4CredentialsUi: Partial<Record<keyof z.infer<typeof Ga4Credentials>, PropertyUI>> = {
  apiSecret: {
    password: true,
  },
};

export type Ga4Credentials = z.infer<typeof Ga4Credentials>;

export const HubspotCredentials = z.object({
  accessToken: z
    .string()
    .describe(
      [
        "To obtain an access secret, go to <b>Settings » Account Setup » Private Apps</b>, create a new private app copy the <b>Access token</b>. See <a href='https://developers.hubspot.com/docs/api/private-apps'>detailed guide</a>.",
        "Please make sure to grant an application all read and write permissions under CRM section",
      ].join("\n")
    ),
  sendPageViewEvents: z
    .boolean()
    .optional()
    .describe("When enabled, Jitsu will send page view events to hubspot (only events with a known email)"),
  autoCreateCustomProperties: z
    .boolean()
    .optional()
    .describe(
      "When enabled, Jitsu will automatically create HubSpot <a href='https://knowledge.hubspot.com/properties/create-and-edit-properties'>custom properties</a> for Contacts and Companies to capture every new trait. Otherwise, only known properties are sent."
    ),
});

export const HubspotCredentialsConfigUi: Partial<Record<keyof z.infer<typeof HubspotCredentials>, PropertyUI>> = {
  accessToken: {
    password: true,
  },
};

export type HubspotCredentials = z.infer<typeof HubspotCredentials>;

export const KLAVIYO_API_VERSIONS = [
  "2023-09-15",
  "2024-02-15",
  "2024-05-15",
  "2024-06-15",
  "2024-07-15",
  "2024-10-15",
  "2025-01-15",
  "2025-04-15",
  "2025-07-15",
  "2025-10-15",
] as const;

export const KlaviyoCredentials = z.object({
  apiKey: z
    .string()
    .describe("You can find this by going to Klaviyo's UI and clicking Account > Settings > API Keys > Create API Key"),
  apiVersion: z
    .enum(KLAVIYO_API_VERSIONS)
    .default(KLAVIYO_API_VERSIONS[KLAVIYO_API_VERSIONS.length - 1])
    .describe(
      "Klaviyo API revision date. Each revision is supported for 2 years. See <a href='https://developers.klaviyo.com/en/docs/api_versioning_and_deprecation_policy' target='_blank' rel='noreferrer noopener'>API versioning policy</a>"
    ),
  includeExternalId: z
    .boolean()
    .optional()
    .default(true)
    .describe(
      "Include the external_id (userId) field in profile data sent to Klaviyo. Disable this if you don't want to sync user IDs to Klaviyo."
    ),
});

export const KlaviyoCredentialsConfigUi: Partial<Record<keyof z.infer<typeof KlaviyoCredentials>, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

export type KlaviyoCredentials = z.infer<typeof KlaviyoCredentials>;

export const AttentiveCredentials = z.object({
  apiKey: z
    .string()
    .describe(
      "A <a href='https://docs.attentive.com/pages/create-and-manage-custom-apps/'>custom app</a> is required to generate an API Key"
    ),
});

export const AttentiveCredentialsConfigUi: Partial<Record<keyof z.infer<typeof AttentiveCredentials>, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

export type AttentiveCredentials = z.infer<typeof AttentiveCredentials>;

export const TikTokConversionsDestinationConfig = z.object({
  accessToken: z
    .string()
    .describe(
      "Your TikTok Access Token. Please see TikTok’s <a href='https://ads.tiktok.com/marketing_api/docs?id=1701890979375106'>Events API documentation</a> for information on how to generate an access token via the TikTok Ads Manager or API."
    ),
  pixelCode: z
    .string()
    .describe(
      "Your TikTok Pixel ID. Please see TikTok’s <a href='https://ads.tiktok.com/marketing_api/docs?id=1701890979375106'>Events API documentation</a> for information on how to find this value."
    ),
  testEventCode: z
    .string()
    .optional()
    .describe("The Test Event Code can be found in the events manager of the TikTok ads platform."),
  productContentIdField: z
    .enum(["sku", "variant_id", "product_id", "id"])
    .optional()
    .describe(
      "Which product property to use as `content_id` when sending events to TikTok. If left empty, a fallback chain is used: sku → product_id → id. If you select a specific field, only that field will be used."
    ),
  dedupSourceProperty: z
    .string()
    .optional()
    .describe(
      "Optional dot-notation path (e.g., <code>properties.order_id</code>) used to derive <code>event_id</code> for deduplication with the TikTok Pixel. When unset, falls back to the event's <code>messageId</code>. Configure the same value on the TikTok Pixel destination for browser/server event deduplication to work."
    ),
});

export const TikTokConversionsDestinationConfigUi: Partial<
  Record<keyof z.infer<typeof TikTokConversionsDestinationConfig>, PropertyUI>
> = {
  accessToken: {
    password: true,
  },
};

export type TikTokConversionsCredentials = z.infer<typeof TikTokConversionsDestinationConfig>;

export const PinterestTagCredentials = z.object({
  tagId: z.string().describe("Enter your Pinterest Tag ID. It should be a series of numbers, like 123456789."),
  useEnhancedMatchLoading: z
    .boolean()
    .optional()
    .describe(
      "If this setting is enabled, the Pinterest tag will be loaded with the existing traits of the logged user (if available). See the <a target='_blank' href='https://developers.pinterest.com/docs/overview/welcome/'>official documentation</a> for more information."
    )
    .default(false),
});

export type PinterestTagCredentials = z.infer<typeof PinterestTagCredentials>;

export const PinterestConversionsDestinationConfig = z.object({
  appName: z.string().describe("The name of the Site from which the event originated."),
  adAccountId: z
    .string()
    .describe(
      "Unique identifier of an ad account. This can be found in the Pinterest UI by following the steps mentioned <a href='https://developers.pinterest.com/docs/conversions/conversions/#Find%20your%20%2Cad_account_id'>here</a>."
    ),
  conversionToken: z
    .string()
    .describe(
      "The conversion token for your Pinterest account. This can be found in the Pinterest UI by following the steps mentioned <a href='https://developers.pinterest.com/docs/conversions/conversions/#Get%20the%20conversion%20token'>here</a>."
    ),
  testMode: z
    .boolean()
    .optional()
    .describe("Enable test mode, which shows all events in a debug tool on the Pinterest UI"),
});

export const PinterestConversionsDestinationConfigUi: Partial<
  Record<keyof z.infer<typeof PinterestConversionsDestinationConfig>, PropertyUI>
> = {
  conversionToken: {
    password: true,
  },
};

export type PinterestConversionsCredentials = z.infer<typeof PinterestConversionsDestinationConfig>;

export const SnapConversionsDestinationConfig = z.object({
  pixelId: z.string().describe("The Pixel ID for your Snapchat Ad Account. Required for web and offline events."),
  accessToken: z
    .string()
    .describe(
      "The generated Conversions API Token for your Snapchat Ads account found in the Business Details section."
    ),
  testMode: z
    .boolean()
    .optional()
    .describe(
      "Enable test mode, which appends '/validate' to the endpoint which shows all events in a debug tool on the Snapchat UI"
    ),
  productContentIdField: z
    .enum(["sku", "product_id", "variant_id"])
    .default("sku")
    .describe(
      "Product Content ID Field. Specify which product field to use as item_id when sending events to Snapchat. This value must match the Item ID in your Snap catalog. For Shopify stores using the native catalog sync, use variant_id."
    ),
});

export const SnapConversionsDestinationConfigUi: Partial<
  Record<keyof z.infer<typeof SnapConversionsDestinationConfig>, PropertyUI>
> = {
  accessToken: {
    password: true,
  },
};

export type SnapConversionsCredentials = z.infer<typeof SnapConversionsDestinationConfig>;

export const TikTokPixelCredentials = z.object({
  pixelCode: z
    .string()
    .describe(
      "Your TikTok Pixel ID. Please see TikTok's Pixel <a href='https://ads.tiktok.com/marketing_api/docs?id=1739583652957185' target='_blank'>documentation</a> for information on how to find this value."
    ),
  ldu: z
    .boolean()
    .optional()
    .describe(
      'In order to help facilitate advertiser\'s compliance with the right to opt-out of sale and sharing of personal data under certain U.S. state privacy laws, TikTok offers a Limited Data Use ("LDU") feature. For more information, please refer to TikTok\'s <a href="https://business-api.tiktok.com/portal/docs?id=1770092377990145" target="_blank">documentation page</a>.'
    ),
  autoPageView: z
    .boolean()
    .optional()
    .default(true)
    .describe('If true, TikTok Pixel will fire a "Pageview" event when the pixel is loaded on the page.'),
  productContentIdField: z
    .enum(["sku", "variant_id", "product_id", "id"])
    .default("sku")
    .describe(
      "Define the parameter name for the content ID. If not provided, the default waterfall is sku, product_id then id."
    ),
  dedupSourceProperty: z
    .string()
    .optional()
    .describe(
      "Optional dot-notation path (e.g., <code>properties.order_id</code>) used to derive <code>event_id</code> for deduplication with the TikTok Conversions API. When unset, falls back to the event's <code>messageId</code>. Configure the same value on the TikTok Conversions API destination for browser/server event deduplication to work."
    ),
});

export type TikTokPixelCredentials = z.infer<typeof TikTokPixelCredentials>;

export const BingAdsCredentials = z.object({
  tagId: z
    .string()
    .describe("Your Bing Universal Event Tracking Tag ID. The ID can be found in your Microsoft Ads dashboard."),
  adConsentCategory: z
    .string()
    .optional()
    .describe("Consent category to map to the Microsoft UET ad_storage consent signal."),
});

export type BingAdsCredentials = z.infer<typeof BingAdsCredentials>;

export const MicrosoftCapiCredentials = z.object({
  tagId: z
    .string()
    .describe(
      "Your Microsoft UET Tag ID. This can be found in your Microsoft Ads dashboard under Conversion tracking > UET tags. You can also provision a new tag. We recommend creating a dedicated tag for the Conversions API."
    ),
  accessToken: z
    .string()
    .describe(
      "Your Microsoft CAPI access token. Select <b>Use Conversions API</b> on your UET tag in the Microsoft Ads dashboard to generate a token. Contact your account manager if this option is not available."
    ),
  siteUrl: z
    .string()
    .optional()
    .describe(
      "Your storefront URL, e.g. <code>https://www.example.com</code>. Microsoft requires a non-empty <code>eventSourceUrl</code> on every event. Browser events supply this automatically, but server-side (back-end) events do not — this value is used as the fallback so back-end connections (e.g. Shopify Order Completed) are accepted. Required for any back-end connection."
    ),
  eventMapping: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      "Optional override of the <code>eventName</code> (the event action Microsoft uses for conversion-goal matching) sent for a given Jitsu event. By default the event's own name is sent (e.g. <code>Order Completed</code>), matching the Bing UET device-mode pixel so both surfaces share one conversion goal. Map an event to send a different action string — e.g. <code>Order Completed</code> → <code>order_completed</code> — to match a goal that expects that value. Matching in Microsoft Ads is case-sensitive."
    ),
  adConsentCategory: z
    .string()
    .optional()
    .describe(
      "Consent category to map to the Microsoft CAPI <code>adStorageConsent</code> signal. When unset, or when an event carries no preference for the category, the field is omitted and Microsoft treats the event as granted."
    ),
});

export const MicrosoftCapiCredentialsUi: Partial<Record<keyof z.infer<typeof MicrosoftCapiCredentials>, PropertyUI>> = {
  accessToken: {
    password: true,
  },
};

export type MicrosoftCapiCredentials = z.infer<typeof MicrosoftCapiCredentials>;

export const RoktCredentials = z.object({
  apiKey: z
    .string()
    .describe(
      "Your Rokt Events API key. Rokt provisions this for you — contact your Rokt account manager if you do not have one."
    ),
  apiSecret: z.string().describe("Your Rokt Events API secret, issued alongside the API key."),
  eventMapping: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      "Optional override of the Rokt <code>conversiontype</code> sent for a given Jitsu event. Rokt accepts only <code>purchase</code>, <code>signup</code>, <code>subscription</code> and <code>screen_view</code>; any other value is ignored. Defaults: <code>Order Completed</code> → <code>purchase</code>, <code>Signed Up</code> and <code>User Created</code> → <code>signup</code>, <code>Subscription Created</code> → <code>subscription</code>, page views → <code>screen_view</code>. Events with no mapping are skipped."
    ),
  sendPageViewEvents: z
    .boolean()
    .optional()
    .default(true)
    .describe(
      "Send page views to Rokt as <code>screen_view</code> conversions, which Rokt uses for retargeting audiences. Disable to send only purchase, signup and subscription conversions — page views are high volume."
    ),
  apiUrl: z
    .string()
    .optional()
    .describe(
      "Optional endpoint override. Defaults to Rokt's documented <code>https://s2s.us2.mparticle.com/v2/events</code>. Only set this if Rokt has provisioned your account on a different region."
    ),
});

export const RoktCredentialsUi: Partial<Record<keyof z.infer<typeof RoktCredentials>, PropertyUI>> = {
  apiSecret: {
    password: true,
  },
};

export type RoktCredentials = z.infer<typeof RoktCredentials>;

export const RoktPixelCredentials = z.object({
  apiKey: z
    .string()
    .optional()
    .describe(
      "API Key::Your Rokt API key, if your Rokt snippet is built around one. It is the value that appears in the SDK script path on <code>apps.rokt-api.com</code>. Supply this <b>or</b> an Account ID, not both. It is <b>not</b> the Events API key/secret pair used by the Rokt Events API destination."
    ),
  accountId: z
    .string()
    .optional()
    .describe(
      "Account ID::Your Rokt Account ID, if your Rokt snippet is built around <code>launcher.js</code> and <code>createLauncher</code>. Rokt runs two browser SDKs and provisions accounts for one or the other — paste whichever value <b>my.rokt.com &gt; Integrations &gt; Set up the Snippet</b> gave you, and Chord loads the matching integration. Note the Account ID integration asks Rokt for an offer placement on each conversion, so Rokt may render an overlay or embedded unit on your confirmation page."
    ),
  emailFormat: z
    .enum(["raw", "sha256"])
    .optional()
    .default("raw")
    .describe(
      "Email Format::Which identifier slot the shopper's email is sent in. <code>raw</code> sends the plain address in <code>email</code>; <code>sha256</code> sends a hashed address in Rokt's <code>other</code> field. Rokt matches on either and their setup wizard recommends raw, which is the default here. Choose <code>sha256</code> if your security policy forbids a plain address in the page's network traffic."
    ),
  isDevelopmentMode: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Development Mode::Sends events to Rokt's development data environment instead of production. Use it while testing the integration, then turn it off — Rokt does not report on development data."
    ),
  eventMapping: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      "Custom Event Mapping::Optional. Route a Chord event onto a Rokt <code>conversiontype</code> — e.g. <code>Trial Started</code> → <code>subscription</code>. Keep this identical to the mapping on the Rokt Events API destination if you run both, or the two surfaces will classify the same conversion differently. Events with no mapping are skipped."
    ),
});

export const RoktPixelCredentialsUi: Partial<Record<keyof z.infer<typeof RoktPixelCredentials>, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

export type RoktPixelCredentials = z.infer<typeof RoktPixelCredentials>;

export const OpenAiAdsConversionsCredentials = z.object({
  pixelId: z
    .string()
    .describe(
      "Pixel ID::Your OpenAI Ads pixel ID, created in OpenAI Ads Manager under <b>Tools &gt; Conversions</b>. Use the <b>same</b> pixel ID as the OpenAI Ads Pixel destination — sharing it is what lets OpenAI deduplicate events across both."
    ),
  apiKey: z
    .string()
    .describe(
      "API Key::Your OpenAI Ads <b>Conversion Key</b>, created in OpenAI Ads Manager under <b>Tools &gt; Conversions</b> alongside the Pixel ID. <b>Not</b> the API key under <b>Settings</b> — that one authenticates OpenAI's management API and is rejected here. The Conversion Key is per ad account, so one key covers every pixel in the account."
    ),
  siteUrl: z
    .string()
    .optional()
    .describe(
      "Storefront URL::Your storefront URL, e.g. <code>https://www.example.com</code>. OpenAI requires a <code>source_url</code> on every web event. Browser events supply this automatically, but server-side (back-end) events do not — this value is used as the fallback so back-end connections (e.g. Shopify Order Completed) are accepted instead of rejected with <code>source_url_required_for_web</code>. Required for any back-end connection."
    ),
  eventMapping: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      "Custom Event Mapping::Optional. Route a Chord event with no standard OpenAI equivalent onto a custom event you have registered in Ads Manager — e.g. <code>Product Added to Wishlist</code> → <code>wishlist-added</code>. Names are lowercased and spaces become dashes; the result must be 1-64 characters of lowercase letters, numbers, underscores or dashes. An unregistered name is discarded by OpenAI, so only map events you have created there first."
    ),
  validateOnly: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Validate Only::When enabled, OpenAI validates each event and records nothing. Use it to confirm an integration end to end without affecting reporting, then turn it off. Leave off for live traffic."
    ),
});

export const OpenAiAdsConversionsCredentialsUi: Partial<
  Record<keyof z.infer<typeof OpenAiAdsConversionsCredentials>, PropertyUI>
> = {
  apiKey: {
    password: true,
  },
};

export type OpenAiAdsConversionsCredentials = z.infer<typeof OpenAiAdsConversionsCredentials>;

export const OpenAiAdsPixelCredentials = z.object({
  pixelId: z
    .string()
    .describe(
      "Pixel ID::Your OpenAI Ads pixel ID, created in OpenAI Ads Manager under <b>Tools &gt; Conversions</b>. Use the same pixel ID as the OpenAI Ads Conversions API destination. This destination needs no key of its own — the Conversion Key shown beside the Pixel ID belongs to the Conversions API destination."
    ),
  enableAdvancedMatching: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Enable Advanced Matching::Sends the visitor's email, customer ID and name to OpenAI as SHA-256 hashes to improve conversion matching. Nothing is sent in plaintext."
    ),
  eventMapping: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      "Custom Event Mapping::Optional. Route a Chord event with no standard OpenAI equivalent onto a custom event you have registered in Ads Manager — e.g. <code>Product Added to Wishlist</code> → <code>wishlist-added</code>. Names are lowercased and spaces become dashes; the result must be 1-64 characters of lowercase letters, numbers, underscores or dashes. An unregistered name is discarded by OpenAI, so only map events you have created there first. Use the <b>same</b> mapping as the OpenAI Ads Conversions API destination, or the two surfaces will send the same conversion under different names instead of deduplicating it."
    ),
  debug: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Debug Logging::Enables the OpenAI pixel's own console logging in the browser. Useful while verifying an installation; leave off in production."
    ),
});

export type OpenAiAdsPixelCredentials = z.infer<typeof OpenAiAdsPixelCredentials>;

export const NostoCredentials = z.object({
  apiToken: z
    .string()
    .describe(
      "API Token::Your Nosto <b>Apps</b> token, created in Nosto under <b>Settings &gt; Authentication Tokens</b>. The token is scoped to one Nosto account, so it must come from the <b>same account</b> as the Account ID on the Nosto Tag destination — that is what makes both surfaces write to a single visitor session instead of two."
    ),
});

export const NostoCredentialsUi: Partial<Record<keyof z.infer<typeof NostoCredentials>, PropertyUI>> = {
  apiToken: {
    password: true,
  },
};

export type NostoCredentials = z.infer<typeof NostoCredentials>;

export const NostoTagCredentials = z.object({
  accountId: z
    .string()
    .describe(
      "Account ID::Your Nosto account ID, shown in the top-right corner of the Nosto dashboard under the shop name. It is used to load Nosto's script, so it must be correct for recommendations to render at all."
    ),
  loadRecommendations: z
    .boolean()
    .optional()
    .default(true)
    .describe(
      "Load Recommendations::Requests and renders Nosto recommendations into the placements already present on the page. Leave on unless you only want Nosto to receive behavioural data — Nosto discovers placements from your page markup, so no configuration is needed here."
    ),
});

export type NostoTagCredentials = z.infer<typeof NostoTagCredentials>;

export const RedditConversionsCredentials = z.object({
  pixelId: z
    .string()
    .describe(
      "Pixel ID::Your Reddit pixel ID, found in the Reddit Ads dashboard under Events Manager. Starts with <code>t2_</code>. This is the same pixel ID used by the Reddit Pixel destination — sharing it is what lets Reddit deduplicate events across both."
    ),
  conversionToken: z
    .string()
    .describe(
      "Conversion Token::Your Reddit conversion access token. Generate it in the Reddit Ads dashboard under Events Manager > Conversions API."
    ),
  testId: z
    .string()
    .optional()
    .describe(
      "Test ID::Optional. When set, events are attributed to this Reddit test ID instead of production reporting. Leave empty for live traffic."
    ),
});

export const RedditConversionsCredentialsUi: Partial<
  Record<keyof z.infer<typeof RedditConversionsCredentials>, PropertyUI>
> = {
  conversionToken: {
    password: true,
  },
};

export type RedditConversionsCredentials = z.infer<typeof RedditConversionsCredentials>;

export const RedditPixelCredentials = z.object({
  pixelId: z
    .string()
    .describe(
      "Pixel ID::Your Reddit pixel ID, found in the Reddit Ads dashboard under Events Manager. Starts with <code>t2_</code>."
    ),
  enableAdvancedMatching: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Enable Advanced Matching::Sends the visitor's email and customer ID to Reddit to improve conversion matching. Reddit honours only the first initialization on a page, so this covers visitors who are already signed in when the page loads."
    ),
});

export type RedditPixelCredentials = z.infer<typeof RedditPixelCredentials>;

/**
 * Dotdigital's regional pods. An account lives in exactly one and the region is part of every API
 * hostname, so there is no way to discover it at runtime — it has to be configured.
 */
const dotdigitalRegion = z
  .enum(["r1", "r2", "r3"])
  .default("r1")
  .describe(
    "Region::The Dotdigital region your account is hosted in. Find it in the API endpoint shown under Account > Settings > Access > API users — <code>r1</code>, <code>r2</code> or <code>r3</code>."
  );

/**
 * Shared by both Dotdigital surfaces, and **required** — not because Dotdigital demands a
 * cart-level link (its validator does not: it reported three missing fields at once on a cart whose
 * `cartUrl` was empty and did not mention it), but because Dotdigital *does* require a `url` on
 * every cart **line**, and Chord's tracking plan has no per-line url field at all (CHO-3807). This
 * is the last resort in that chain. Without it, a cart whose lines carry no urls of their own is
 * rejected outright — so an optional field here means cart tracking silently does not work.
 */
const dotdigitalCartUrl = z
  .string()
  .min(1)
  .describe(
    "Cart URL::Your storefront's cart page URL. Required: Dotdigital rejects a cart update unless every line has a product URL, and this is what Chord falls back to for a line that has none. It is also the link a shopper follows out of an abandoned cart email."
  );

export const DotdigitalTagCredentials = z.object({
  tagUrl: z
    .string()
    .describe(
      "Tag URL::Your Dotdigital Tag URL. In Dotdigital go to <b>Connect > Connect website</b>, select <b>View code</b>, and copy the <code>src</code> value from the script tag. It looks like <code>https://r1.ddlnk.net/DM-0000000000-01/ddgtag</code>, or <code>https://emails.yourbrand.com/DM-0000000000-01/ddgtag.js</code> if your account uses a branded domain. Paste it whole — the host varies by account, and it is the host your storefront's CSP must allow. If you already installed the Dotdigital Tag by hand, remove it — running two tags doubles every page view."
    ),
  cartDelay: z
    .number()
    // `.nullish()`, not `.optional()`: an empty numeric field arrives as null rather than absent.
    // The console posts null for a cleared input, and Hub's form transform runs parseInt("") -> NaN,
    // which JSON.stringify writes as null. `.optional()` rejects both and the save fails validation.
    // No `.default()` on purpose — Dotdigital applies the account's own delay when none is sent, and
    // inventing one here would assert a setting the operator did not make.
    .nullish()
    .describe(
      "Abandoned Cart Delay (minutes)::How long after the last cart update Dotdigital waits before enrolling the contact into your abandoned cart program. Leave empty to use your account default."
    ),
  abandonedCartProgramId: z
    .string()
    .optional()
    .describe(
      "Abandoned Cart Program ID::The Dotdigital program to enrol an abandoned cart into. Find it in the URL when you open the program under <b>Automation > Programs</b>."
    ),
  siteBrand: z
    .string()
    .optional()
    .describe("Site Brand::Required only if your account uses Dotdigital Personalization. Leave empty otherwise."),
  cartUrl: dotdigitalCartUrl,
  sendCustomEvents: z
    .boolean()
    .optional()
    .default(true)
    .describe(
      "Send Custom Events::Forwards events Chord has no specific Dotdigital mapping for under their own name, creating an Insight data collection for each. Requires the CXDP package on your Dotdigital account — turn this off if you do not have it, because the calls fail silently in the browser."
    ),
});

export type DotdigitalTagCredentials = z.infer<typeof DotdigitalTagCredentials>;

export const DotdigitalCredentials = z.object({
  apiUsername: z
    .string()
    .describe(
      "API User Name::The auto-generated username of a Dotdigital API user. Create one under <b>Account > Settings > Access > API users</b>. Use a dedicated API user for Chord rather than sharing one, so it can be revoked on its own."
    ),
  apiPassword: z
    .string()
    .describe("API User Password::The password you set on that API user. Dotdigital does not show it again."),
  region: dotdigitalRegion,
  identifierType: z
    .enum(["email", "mobileNumber"])
    .default("email")
    .describe(
      "Contact Identifier::Which identifier Dotdigital should key contacts on. Email is the usual choice; pick mobile number only if your Dotdigital contacts are keyed on SMS. Mobile numbers must be in E.164 format (<code>+14155550123</code>) or Chord cannot send them."
    ),
  listId: z
    .number()
    // Same null-vs-undefined reason as cartDelay above.
    .nullish()
    .describe(
      "Address Book ID::Optional. When set, contacts Chord creates are added to this address book — find the ID in the URL when you open it under <b>Audience > Lists</b>. Adding a contact to an address book makes them mailable, so set this only where your consent flow allows it. Leave empty to create contacts without list membership."
    ),
  autoCreateContacts: z
    .boolean()
    .optional()
    .default(true)
    .describe(
      "Create Contacts Automatically::Dotdigital only accepts events for contacts that already exist, so Chord creates or updates the contact before sending each event. Turn this off only if your contacts are provisioned in Dotdigital by another system — with it off, events for unknown contacts are rejected."
    ),
  cartUrl: dotdigitalCartUrl,
});

export const DotdigitalCredentialsUi: Partial<Record<keyof z.infer<typeof DotdigitalCredentials>, PropertyUI>> = {
  apiPassword: {
    password: true,
  },
};

export type DotdigitalCredentials = z.infer<typeof DotdigitalCredentials>;

export const ContentsquarePixelCredentials = z.object({
  siteId: z
    .string()
    .describe(
      "Your Contentsquare (Hotjar) Site ID. This can be found in your Contentsquare dashboard under <b>Get started > Tag installation</b>. Copy just the code between the final '/' and '.js'. For example, if the script tag is shown as <code>&lt;script src=\"https://t.contentsquare.net/uxa/d44639f32ad91.js\"&gt;&lt;/script&gt;</code>, insert just <code>d44639f32ad91</code>."
    ),
  dynamicVariables: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      "Event properties to forward as Contentsquare Dynamic Variables on page events. Each entry maps a source property path (e.g. <code>properties.page_name</code>) to a Contentsquare variable key."
    ),
});

export type ContentsquarePixelCredentials = z.infer<typeof ContentsquarePixelCredentials>;

export const NinetailedCredentials = z.object({
  sendPageEvents: z
    .boolean()
    .optional()
    .default(true)
    .describe("If enabled, all page view events will be sent to Ninetailed."),
});

export type NinetailedCredentials = z.infer<typeof NinetailedCredentials>;

export const NinetailedCloudDestinationConfig = z.object({
  organizationId: z
    .string()
    .describe("The ID of the organization in Ninetailed. This can be found in your Ninetailed dashboard."),
  environmentSlug: z
    .string()
    .describe("The slug of the environment in Ninetailed. This can be found in your Ninetailed dashboard.")
    .default("main"),
});

export type NinetailedCloudCredentials = z.infer<typeof NinetailedCloudDestinationConfig>;

export const ImpactCredentials = z.object({
  campaignId: z.string().optional().describe("Impact Campaign ID"),
  accountSid: z.string().optional().describe("Account SID for authentication"),
  apiKey: z.string().optional().describe("API Key for authentication"),
  eventTypeId: z.string().optional().describe("Custom event type ID"),
  enablePageEvents: z.boolean().optional().describe("Whether to track page view events"),
  enableIdentifyEvents: z.boolean().optional().describe("Whether to track identify events"),
  enableAllTrackEvents: z.boolean().optional().describe("Whether to process all track events as conversions"),
  customParameterMapping: z.record(z.string(), z.string()).optional().describe("Custom field mappings"),
  customMappingForProducts: z.record(z.string(), z.string()).optional().describe("Custom product field mappings"),
  actionEventNames: z.array(z.string()).optional().describe("Custom event names for conversions"),
  pageLoadEventNames: z.array(z.string()).optional().describe("Custom event names for page views"),
});

export const ImpactCredentialsUi: Partial<Record<keyof z.infer<typeof ImpactCredentials>, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};
export type ImpactCredentials = z.infer<typeof ImpactCredentials>;

export const IterableCredentials = z.object({
  apiKey: z
    .string()
    .describe(`Your Iterable API Key. You can find this in the Iterable UI under Integrations > API Keys.`),
  userIdentifier: z
    .enum(["userId", "email"])
    .optional()
    .default("userId")
    .describe(
      `User Identifier::How your Iterable project identifies users. Find it in Iterable under <b>Settings > Project > Unique identifier</b>, where it reads either "Email-based project (unique email)" or a userId-based equivalent. Iterable accepts an email <b>or</b> a userId on every call, never both, and the one that resolves is fixed by that project setting — sending the other fails with <code>UnknownUserIdError</code> and the event is dropped. Note that an email-based project still resolves a userId for profiles that happen to have one, so a partial failure rate does not mean this is set correctly. See <a target='_blank' href='https://support.iterable.com/hc/en-us/articles/29156459027348'>Creating and Identifying Users with Iterable APIs</a>.`
    ),
});

export const IterableCredentialsUi: Partial<Record<keyof z.infer<typeof IterableCredentials>, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

export type IterableCredentials = z.infer<typeof IterableCredentials>;

export const DelightedCredentials = z.object({
  apiKey: z.string().describe("You can find this by going to Delighted's UI and clicking Integrations > API."),
  delay: z
    .number()
    .optional()
    .default(0)
    .describe("Delay (in seconds) before sending the survey. Useful for webhooks."),
});

export const DelightedCredentialsUi: Partial<Record<keyof z.infer<typeof DelightedCredentials>, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

export type DelightedCredentials = z.infer<typeof DelightedCredentials>;

export const AlgoliaCredentials = z.object({
  appId: z
    .string()
    .describe(
      `Your Algolia Application ID. Your app ID the API Keys section of your account settings in the Algolia dashboard.`
    ),
  apiKey: z
    .string()
    .describe(
      `Your Algolia API key. Your API key can be found in the API Keys section of your account settings in the Algolia dashboard. Please provide a Write API key from Algolia.`
    ),
});

export const AlgoliaCredentialsUi: Partial<Record<keyof z.infer<typeof AlgoliaCredentials>, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

export type AlgoliaCredentials = z.infer<typeof AlgoliaCredentials>;

export const TwitterAdsCredentials = z.object({
  pixelId: z
    .string()
    .describe("Twitter Pixel ID from your Twitter Ads account (e.g., 'abcd'). Find this in Events Manager."),
  eventMapping: z
    .record(z.string(), z.string())
    .optional()
    .describe("Map Jitsu event names to Twitter Event IDs (e.g., 'Product Added' -> 'tw-qkno4-qkni5')."),
  identifier: z
    .string()
    .default("product_id")
    .describe(
      "Product field to use for content_id. Common values: 'product_id', 'sku', 'id'. Defaults to 'product_id'."
    ),
});

export type TwitterAdsCredentials = z.infer<typeof TwitterAdsCredentials>;

export const CdpLoopbackCredentials = z.object({
  sourceWriteKey: z
    .string()
    .describe(
      "Site Write Key - Enter the full write key (format: key_id:secret) of the site that will receive the re-injected events. You can copy this from the site's Private Keys section."
    ),
});

export const CdpLoopbackCredentialsUi: Partial<Record<keyof z.infer<typeof CdpLoopbackCredentials>, PropertyUI>> = {
  sourceWriteKey: {
    displayName: "Site Write Key",
    documentation: "The server write key for the site to receive the loopback events",
    password: true,
  },
};

export type CdpLoopbackCredentials = z.infer<typeof CdpLoopbackCredentials>;

export const AmplitudePixelCredentials = z.object({
  apiKey: z.string().describe("Amplitude API Key::Your Amplitude project's API Key"),

  // Autocapture Features (SDK v2)
  autocapture: z
    .object({
      attribution: z
        .boolean()
        .optional()
        .default(true)
        .describe(
          "Attribution Tracking::Automatically capture UTM parameters, referrer, and click IDs (gclid, fbclid, etc.). Essential for marketing attribution."
        ),
      sessions: z
        .boolean()
        .optional()
        .default(true)
        .describe(
          "Session Tracking::Automatically send session start and end events. Provides better session analytics and enables cloud mode session sharing."
        ),
      pageViews: z
        .boolean()
        .optional()
        .default(false)
        .describe(
          "Auto Page Views::Automatically track page views on SPA navigation. Disable if using manual page tracking (trackAllPages, trackNamedPages) to avoid duplicates."
        ),
      elementInteractions: z
        .boolean()
        .optional()
        .default(false)
        .describe(
          "Element Interactions::Track clicks and changes on page elements automatically. Useful for understanding user behavior without manual instrumentation."
        ),
      formInteractions: z
        .boolean()
        .optional()
        .default(false)
        .describe(
          "Form Interactions::Automatically track form submissions and field interactions. Captures form engagement metrics."
        ),
      fileDownloads: z
        .boolean()
        .optional()
        .default(false)
        .describe(
          "File Downloads::Track clicks on file download links (PDF, Excel, Word, etc.). Useful for content engagement tracking."
        ),
    })
    .optional()
    .describe(
      "Autocapture Features::Enable automatic event tracking without manual instrumentation. Uses Amplitude Browser SDK v2. <a href='https://amplitude.com/docs/data/sdks/browser-2/autocapture' target='_blank'>Learn more</a>"
    ),

  // Manual Page Tracking (can coexist with autocapture)
  trackAllPages: z
    .boolean()
    .optional()
    .default(false)
    .describe("Track All Pages::Send a 'Loaded a Page' event for every page() call"),
  trackNamedPages: z
    .boolean()
    .optional()
    .default(true)
    .describe("Track Named Pages::Send a 'Viewed [Name] Page' event when page name is provided"),
  trackCategorizedPages: z
    .boolean()
    .optional()
    .default(true)
    .describe("Track Categorized Pages::Send a 'Viewed [Category] Page' event when page category is provided"),

  // Core Configuration
  preferAnonymousIdForDeviceId: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Use Anonymous ID as Device ID::Use Jitsu's anonymousId as Amplitude's device_id for cross-platform tracking"
    ),
  sessionTimeout: z
    .number()
    .optional()
    .default(30)
    .describe("Session Timeout (minutes)::Duration of inactivity before starting a new session"),
  minIdLength: z
    .number()
    .optional()
    .default(5)
    .describe(
      "Minimum ID Length::Minimum character length for user and device IDs. SDK v2 default is 5. Set to 1 if you have shorter user IDs."
    ),
  serverZone: z
    .enum(["US", "EU"])
    .optional()
    .default("US")
    .describe("Server Zone::Data residency region. US sends to api2.amplitude.com, EU sends to api.eu.amplitude.com"),
  injectAmplitude: z
    .boolean()
    .optional()
    .default(true)
    .describe(
      "Inject Amplitude SDK::When enabled (default), Chord loads and initializes the Amplitude SDK automatically. Disable this when the Amplitude SDK is already loaded natively in your theme (e.g. via a &lt;script&gt; tag), so Chord forwards events to the existing instance without reinitializing it. Disabling allows Amplitude autocapture, Session Replay, and Web Experimentation to initialize as early as possible."
    ),

  // Action Mappings
  actionMappings: z
    .array(
      z.object({
        sourceEvent: z
          .string()
          .describe(
            "Source Event(s)::Event(s) to match. Comma-separated for multiple (e.g., <code>Purchase,Subscription,Renewal</code>). Use event name for track events, type for others (identify, page), or <code>*</code> for all."
          ),
        action: z
          .enum(["logEventV2", "identifyUser", "logPurchase", "mapUser", "groupIdentifyUser", "logEvent"])
          .describe(
            "Amplitude Action::logEventV2 = Track event, identifyUser = Update user, logPurchase = Track revenue, mapUser = Merge identities, groupIdentifyUser = Update group"
          ),
        targetEvent: z
          .string()
          .optional()
          .describe(
            "Target Event Name::Override the event name sent to Amplitude. Leave empty to use the original name. Use <code>$drop</code> to filter/exclude the event."
          ),
        fields: z
          .array(
            z.object({
              sourceProperty: z
                .string()
                .describe(
                  "Source Property::Path in your event. Supports dot notation (properties.product_id) and array wildcards (properties.products[*].sku)."
                ),
              targetProperty: z
                .string()
                .describe(
                  "Target Property::Amplitude field name (e.g., event_properties.item_id, user_properties.$set.plan, revenue)."
                ),
            })
          )
          .optional()
          .describe("Field Mappings::Map properties from your events to Amplitude fields."),
      })
    )
    .optional()
    .describe(
      "Custom Action Mappings::Override default routing. Defaults: track → logEventV2, identify → identifyUser, page/screen → logEventV2, group → groupIdentifyUser, alias → mapUser, Order Completed → logPurchase"
    ),
});

export type AmplitudePixelCredentials = z.infer<typeof AmplitudePixelCredentials>;

export const AmplitudePixelCredentialsUi: Partial<Record<keyof AmplitudePixelCredentials, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

export const NorthbeamCredentials = z.object({
  apiKey: z
    .string()
    .describe("API Key::Your Northbeam API key. Can be found in the Northbeam dashboard under Settings > API Keys."),
  dataClientId: z
    .string()
    .describe(
      "Data Client ID::A UUID used to identify your account. Can be found in the Northbeam dashboard under Settings > API Keys."
    ),
  testMode: z
    .boolean()
    .optional()
    .default(false)
    .describe("Test Mode::When enabled, events are sent to Northbeam's UAT environment instead of production."),
});

export const NorthbeamCredentialsUi: Partial<Record<keyof z.infer<typeof NorthbeamCredentials>, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

export type NorthbeamCredentials = z.infer<typeof NorthbeamCredentials>;

export const EmotiveCredentials = z.object({
  apiToken: z
    .string()
    .describe(
      "API Token::Your Emotive API token, provided by your Emotive contact. Sent as the Authorization header value, prefixed with 'Token '."
    ),
});

export const EmotiveCredentialsUi: Partial<Record<keyof z.infer<typeof EmotiveCredentials>, PropertyUI>> = {
  apiToken: {
    password: true,
  },
};

export type EmotiveCredentials = z.infer<typeof EmotiveCredentials>;

export const SnapPixelCredentials = z.object({
  pixelId: z
    .string()
    .describe(
      "Pixel ID::Your Snap Pixel ID. You can find this in the Snapchat Ads Manager under Events Manager > Pixel Details."
    ),
  productContentIdField: z
    .enum(["sku", "product_id", "variant_id"])
    .default("sku")
    .describe(
      "Product Content ID Field::Specify which product field to use as item_id. Must match the Item ID in your Snap catalog and must be set to the same value as the Snap Conversions API destination. For Shopify stores using the native catalog sync, use variant_id."
    ),
});

export type SnapPixelCredentials = z.infer<typeof SnapPixelCredentials>;

export const SpotifyAdAnalyticsPixelCredentials = z.object({
  pixelKey: z
    .string()
    .describe(
      "Pixel Key::Your Spotify Ad Analytics pixel key (formerly Podsights). Found in your Spotify Ad Analytics dashboard under the pixel installation snippet (the value passed to <code>spdt('conf', { key: ... })</code>)."
    ),
});

export type SpotifyAdAnalyticsPixelCredentials = z.infer<typeof SpotifyAdAnalyticsPixelCredentials>;

export const TheTradeDeskPixelCredentials = z.object({
  advertiserId: z
    .string()
    .describe(
      "Advertiser ID::Your Trade Desk Advertiser ID. Found in your Universal Pixel code snippet (e.g., 'abc123')."
    ),
  pixelId: z
    .string()
    .describe(
      "Universal Pixel ID::Your Trade Desk Universal Pixel ID. Fires on all page views and is used as a fallback for any event below that does not have a dedicated Pixel ID."
    ),
  purchasePixelId: z
    .string()
    .optional()
    .describe(
      "Purchase Pixel ID::Optional. Dedicated Trade Desk Pixel ID for <code>Order Completed</code> events (TTD <code>purchase</code>). Falls back to the Universal Pixel ID if blank."
    ),
  addToCartPixelId: z
    .string()
    .optional()
    .describe(
      "Add to Cart Pixel ID::Optional. Dedicated Trade Desk Pixel ID for <code>Product Added</code> events (TTD <code>addtocart</code>). Falls back to the Universal Pixel ID if blank."
    ),
  productViewedPixelId: z
    .string()
    .optional()
    .describe(
      "Product Viewed Pixel ID::Optional. Dedicated Trade Desk Pixel ID for <code>Product Viewed</code> events (TTD <code>viewitem</code>). Falls back to the Universal Pixel ID if blank."
    ),
  cartViewedPixelId: z
    .string()
    .optional()
    .describe(
      "Cart Viewed Pixel ID::Optional. Dedicated Trade Desk Pixel ID for <code>Cart Viewed</code> events (TTD <code>viewcart</code>). Falls back to the Universal Pixel ID if blank."
    ),
  checkoutStartedPixelId: z
    .string()
    .optional()
    .describe(
      "Checkout Started Pixel ID::Optional. Dedicated Trade Desk Pixel ID for <code>Checkout Started</code> events (TTD <code>startcheckout</code>). Falls back to the Universal Pixel ID if blank."
    ),
  addToWishlistPixelId: z
    .string()
    .optional()
    .describe(
      "Add to Wishlist Pixel ID::Optional. Dedicated Trade Desk Pixel ID for <code>Product Added to Wishlist</code> events (TTD <code>addtowishlist</code>). Falls back to the Universal Pixel ID if blank."
    ),
  productsSearchedPixelId: z
    .string()
    .optional()
    .describe(
      "Products Searched Pixel ID::Optional. Dedicated Trade Desk Pixel ID for <code>Products Searched</code> events (TTD <code>searchitem</code>). Falls back to the Universal Pixel ID if blank."
    ),
});

export type TheTradeDeskPixelCredentials = z.infer<typeof TheTradeDeskPixelCredentials>;

export const HeapCredentials = z.object({
  appId: z
    .string()
    .describe(
      "App ID::Your Heap Environment ID. Found in <b>Account &gt; Manage &gt; Projects</b> in the Heap dashboard."
    ),
  region: z
    .enum(["us", "eu"])
    .optional()
    .default("us")
    .describe("Data Region::Send events to the US or EU Heap endpoint."),
  sendPageEvents: z.boolean().optional().default(true).describe("Send <code>page</code> events as Heap track events"),
  enableAnonymousUserProfiles: z
    .boolean()
    .optional()
    .default(false)
    .describe("If enabled, events from anonymous users (no userId) will be sent to Heap"),
});

export type HeapCredentials = z.infer<typeof HeapCredentials>;

export const HeapDeviceCredentials = z.object({
  appId: z
    .string()
    .describe(
      "App ID::Your Heap Environment ID. Found in <b>Account &gt; Manage &gt; Projects</b> in the Heap dashboard."
    ),
});

export type HeapDeviceCredentials = z.infer<typeof HeapDeviceCredentials>;

export const InsiderOneCredentials = z.object({
  accountName: z
    .string()
    .describe(
      "Account Name::Your Insider One account name. Found in <b>InOne Panel &gt; Settings &gt; Inone Settings &gt; Account Preferences</b>."
    ),
  apiKey: z
    .string()
    .describe(
      "API Key::Your Insider One API key. Found in <b>InOne Panel &gt; Settings &gt; Preferences &gt; Integration Settings</b>."
    ),
  emailAsIdentifier: z
    .boolean()
    .optional()
    .default(false)
    .describe("Treat Email as Identifier::When enabled, the user's email address is also used as an identifier."),
  phoneAsIdentifier: z
    .boolean()
    .optional()
    .default(false)
    .describe("Treat Phone Number as Identifier::When enabled, the user's phone number is also used as an identifier."),
});

export type InsiderOneCredentials = z.infer<typeof InsiderOneCredentials>;

export const InsiderPixelCredentials = z.object({
  partnerName: z
    .string()
    .describe(
      "Partner Name::Your Insider partner name (account slug). This is the subdomain used in the SDK URL, e.g. <b>mystore</b> in <code>mystore.api.useinsider.com</code>."
    ),
  partnerId: z
    .string()
    .optional()
    .describe(
      "Partner ID::Your Insider partner ID. This is the <code>id</code> query parameter appended to the SDK script URL."
    ),
});

export type InsiderPixelCredentials = z.infer<typeof InsiderPixelCredentials>;

export const StatsigCredentials = z.object({
  apiKey: z
    .string()
    .describe(
      "Server Secret Key::Your Statsig <b>server secret key</b> (starts with <code>secret-</code>). Found in <b>Project Settings &gt; API Keys</b>. Do not use a client SDK key here — this destination runs server-side."
    ),
  environmentTier: z
    .enum(["production", "staging", "development"])
    .optional()
    .default("production")
    .describe(
      "Environment Tier::Value sent as <code>user.statsigEnvironment.tier</code>. Events tagged with a non-production tier are excluded from production experiment metrics."
    ),
  customIdKey: z
    .string()
    .optional()
    .default("stableID")
    .describe(
      "Anonymous ID Key::The <code>user.customIDs</code> key that the visitor's <code>anonymousId</code> is sent under. This <b>must</b> match the unit ID your Statsig experiments are randomized on, or server-side events will not join to experiment groups. Leave as <code>stableID</code> unless your storefront passes a different custom ID."
    ),
  valueProperty: z
    .string()
    .optional()
    .describe(
      "Value Property::Event property to send as the Statsig <code>value</code> field, used for revenue-style metrics. If empty, falls back to <code>revenue</code>, then <code>total</code>, then <code>value</code>."
    ),
  emailMode: z
    .enum(["private", "attribute", "omit"])
    .optional()
    .default("private")
    .describe(
      "Email Handling::<b>Private</b> sends email under <code>privateAttributes</code> — usable for targeting but never stored in Statsig's event log. <b>Attribute</b> sends it as <code>user.email</code>, which is logged. <b>Omit</b> never sends it."
    ),
  sendPageEvents: z
    .boolean()
    .optional()
    .default(true)
    .describe("Send <code>page</code> events as a Statsig <code>Page View</code> event"),
  sendIdentifyEvents: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Send <code>identify</code> events::Statsig has no user-profile API, so identify calls are logged as an <code>Identify</code> event with traits in <code>metadata</code>. Off by default to avoid inflating event volume."
    ),
});

export type StatsigCredentials = z.infer<typeof StatsigCredentials>;

export const StatsigCredentialsUi: Partial<Record<keyof StatsigCredentials, PropertyUI>> = {
  apiKey: {
    password: true,
  },
};

/** Amazon's advertiser id (the DSP CFID) is numeric. */
export const AMAZON_ADVERTISER_ID_PATTERN = /^\d+$/;
/** Amazon's dataset-name rule: a letter, then letters, digits, `_` or `-`, 5-100 characters in all. */
export const AMAZON_DATASET_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{4,99}$/;

export const AmazonConversionsCredentials = z.object({
  region: z
    .enum(["NA", "EU", "FE"])
    .optional()
    .default("NA")
    .describe(
      "Region::The Amazon Ads API region your advertiser account belongs to. <b>NA</b> (North America), <b>EU</b> (Europe) or <b>FE</b> (Far East). Events sent to the wrong region are rejected. Note that the <b>EU</b> region additionally requires a consent signal on every event (IP address, Amazon consent, TCF or GPP)."
    ),
  advertiserId: z
    .string()
    .regex(AMAZON_ADVERTISER_ID_PATTERN, "Advertiser ID must be numeric")
    .describe(
      "Amazon Advertiser ID::Your Amazon advertiser account ID, sent as the <code>Amazon-Ads-AccountId</code> header. This must be numeric — use the Amazon DSP CFID, not the Entity ID."
    ),
  clientId: z
    .string()
    .describe(
      "LWA Client ID::The Client ID of your Login with Amazon (LWA) application, from the <b>Amazon Ads API</b> section of the Amazon developer console. Also sent as the <code>Amazon-Ads-ClientId</code> header."
    ),
  clientSecret: z.string().describe("LWA Client Secret::The Client Secret of your Login with Amazon application."),
  refreshToken: z
    .string()
    .describe(
      "LWA Refresh Token::The long-lived refresh token issued when your Amazon Ads API application was authorized for this advertiser account. It is exchanged for a short-lived access token on each request and cached until it expires."
    ),
  dataSetName: z
    .string()
    .regex(
      AMAZON_DATASET_NAME_PATTERN,
      "Dataset Name must start with a letter, use only letters, numbers, _ or -, and be 5-100 characters"
    )
    .optional()
    .describe(
      "Dataset Name::Amazon Ads groups uploaded events into datasets, which is how events appear in Amazon Ads Data Manager. Must start with a letter, contain only letters, numbers, underscores or hyphens, and be 5–100 characters. Defaults to <code>Default_Events</code> when left empty. <b>Changing this on a live destination creates new conversion definitions</b> — set it during initial setup and leave it alone afterwards."
    ),
  eventActionSource: z
    .enum(["website", "android", "ios", "fire_tv", "offline"])
    .optional()
    .default("website")
    .describe(
      "Event Action Source::The platform events are sourced from, sent as <code>eventDescription.eventSource</code>. Together with the event name and conversion type it defines a conversion in Amazon Ads, so treat it as fixed once events are flowing."
    ),
  defaultCountryCode: z
    .string()
    .optional()
    .default("US")
    .describe(
      "Default Country Code::Amazon requires an ISO 3166-1 alpha-2 country code on every event. The country is taken from <code>context.locale</code>, then <code>context.geo</code>, then the customer address; this value is the last resort so back-end events without geo data are still accepted. Clear it to have such events fail loudly instead."
    ),
  sendPageEvents: z
    .boolean()
    .optional()
    .default(true)
    .describe("Send <code>page</code> events as the Amazon <code>PAGE_VIEW</code> conversion type"),
  forwardUnmappedEvents: z
    .boolean()
    .optional()
    .default(true)
    .describe(
      "Forward unmapped events as OTHER::Amazon defines a real <code>OTHER</code> conversion type for actions outside its standard taxonomy, so events without a mapping are forwarded under their own name as <code>OTHER</code>. Turn this off to send only the mapped conversion events — skipped events are logged explicitly either way."
    ),
  limitedDataUse: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Limited Data Use::Stamp every event with the <code>LIMITED_DATA_USE</code> data-processing option. Amazon does <b>not</b> process events marked this way for measurement or optimization, so enable it only when a privacy regime requires it. Individual events can also opt in via a <code>dataProcessingOptions</code> property."
    ),
  eventMapping: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      "Event Mapping::Optional override of the Amazon conversion type used for a given event, e.g. <code>Subscription Renewed</code> → <code>OFF_AMAZON_PURCHASES</code>. Valid values are <code>ADD_TO_SHOPPING_CART</code>, <code>APPLICATION</code>, <code>CHECKOUT</code>, <code>CONTACT</code>, <code>LEAD</code>, <code>OFF_AMAZON_PURCHASES</code>, <code>MOBILE_APP_FIRST_START</code>, <code>PAGE_VIEW</code>, <code>SEARCH</code>, <code>SIGN_UP</code>, <code>SUBSCRIBE</code> and <code>OTHER</code>. An event mapped to an unrecognized value is skipped."
    ),
});

export type AmazonConversionsCredentials = z.infer<typeof AmazonConversionsCredentials>;

export const AmazonConversionsCredentialsUi: Partial<Record<keyof AmazonConversionsCredentials, PropertyUI>> = {
  clientSecret: {
    password: true,
  },
  refreshToken: {
    password: true,
  },
};

/** A Google Ads customer ID: ten digits, with or without the dashes the Google Ads UI shows. */
export const GOOGLE_ADS_CUSTOMER_ID_PATTERN = /^\d{3}-?\d{3}-?\d{4}$/;

export const GoogleAdsConversionsCredentials = z.object({
  customerId: z
    .string()
    .regex(GOOGLE_ADS_CUSTOMER_ID_PATTERN, "Customer ID must be a 10-digit Google Ads customer ID")
    .describe(
      "Customer ID::The Google Ads account that owns the conversion actions, e.g. <code>123-456-7890</code>. Shown at the top of the Google Ads UI."
    ),
  loginCustomerId: z
    .string()
    .regex(GOOGLE_ADS_CUSTOMER_ID_PATTERN, "Manager Account ID must be a 10-digit Google Ads customer ID")
    .optional()
    .or(z.literal(""))
    .describe(
      "Manager Account ID::Only if the service account was added to a manager account rather than to the customer account itself. Leave blank otherwise."
    ),
  serviceAccountKey: z
    .string()
    .describe(
      "Service Account Key::The JSON key of a Google Cloud service account in your own project, with the Data Manager API enabled. Add the service account's email address as a user of your Google Ads account (or its manager account)."
    ),
  conversions: z
    .array(
      z.object({
        eventName: z
          .string()
          .describe("Event Name::The Chord event to send, e.g. <code>Order Completed</code>. Not case-sensitive."),
        conversionActionId: z
          .string()
          .describe(
            "Conversion Action ID::The <b>Conversion type ID</b> shown on the conversion action's Details page in Google Ads (<b>Goals → Conversions</b>). The action's source must be <b>Website</b>."
          ),
      })
    )
    .optional()
    .default([])
    .describe(
      "Conversions::Which Chord events to send, and the conversion action each one counts toward. Map an event to several actions to count it in each. Events not listed here are skipped."
    ),
  testMode: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "Test Mode::Google validates each request and reports problems, but records nothing. Turn off to send real conversions."
    ),
});

export type GoogleAdsConversionsCredentials = z.infer<typeof GoogleAdsConversionsCredentials>;

export const GoogleAdsConversionsCredentialsUi: Partial<Record<keyof GoogleAdsConversionsCredentials, PropertyUI>> = {
  serviceAccountKey: {
    password: true,
  },
};
